import type { DsdNoteReferenceCandidateTone } from "@/utils/dsd-editor/note-reference";
import type {
  DsdEditorSpellcheckBlock as DsdEditorCorrectionBlock,
  DsdEditorParagraphSegment,
} from "@/apis/dsd-editor/route";
import {
  isSumInteractiveCell,
  PlainDsdTableView,
  NOTE_REFERENCE_STATUS_META,
  renderPlainDsdCellText,
} from "@/components/dsd-table-view";
import { DsdReviewHardRuleCountChip } from "./hard-rule-count-chip";
import type {
  PlainDsdCell,
  PlainDsdNoteReferenceStatus,
} from "@/types/plain-dsd-table";
import {
  DsdReviewCorrectionCellDiff,
  DsdReviewCorrectionInlineDiff,
} from "./corrections/correction-diff";
import {
  NOTE_REFERENCE_EVIDENCE_CELL_CLASS,
  NOTE_REFERENCE_EVIDENCE_ROW_CLASS,
  NOTE_REFERENCE_CELL_FILL,
  NOTE_REFERENCE_CELL_FILL_DIM,
  NOTE_REFERENCE_HARD_RULE_CELL_CLASS,
} from "@/utils/dsd-editor/note-reference-colors";
import { cn } from "@/lib/utils";
import { DsdTextBlock } from "@/toggle-ui-kit/dsd-table";
import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";
import { matchedParagraphKey } from "@/utils/dsd-editor/matched-paragraphs";
import type { DsdEditorCorrectionItem } from "@/utils/dsd-editor/spellcheck";
import {
  createContext,
  Fragment,
  memo,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";

const DsdEditorCellDomIdPrefixContext = createContext("dsd-editor-cell");
const useDsdEditorCellDomId = () => {
  const prefix = useContext(DsdEditorCellDomIdPrefixContext);
  return (cellId: string) => `${prefix}-${cellId}`;
};

type DsdEditorNoteReferenceReviewContent = {
  linkedCellIds: ReadonlySet<string>;
  candidateCellIds?: ReadonlySet<string>;
  candidateStatusByCellId?: ReadonlyMap<string, DsdNoteReferenceCandidateTone>;
  statusByCellId: ReadonlyMap<string, PlainDsdNoteReferenceStatus>;
  /** 하드룰 연결의 양쪽 셀 → 걸린 규칙 수(베타). 빗금 채움, 2 이상이면 셀 안 개수 칩 */
  hardRuleCountByCellId?: ReadonlyMap<string, number>;
  /** 상태 → 셀 툴팁 문구 (화면이 소유) */
  statusLabels: Readonly<Record<PlainDsdNoteReferenceStatus, string>>;
  /** 이 셀만 남기고 나머지 주석 색을 흐리게 (선택 or 호버 미리보기) */
  focusCellId?: string | null;
  /** 규칙/값 근거 hover 위치 — 행 근거면 행의 모든 셀 */
  evidencePreviewCellIds?: ReadonlySet<string>;
  /** 규칙 위치는 파랑, 실제 값 비교 대상은 보라, 합산 참여 셀은 주황. match-* 는 근거 창의 후보 셀(상태 톤 200 채움 + 500 테두리) */
  evidencePreviewTone?:
    | "rule"
    | "value"
    | "explanation"
    | "match-positive"
    | "match-caution"
    | "match-negative";
  /** 근거 창: 후보 셀이 속한 행 전체 (상태 톤 100 채움). match-* 톤과 함께 쓴다 */
  evidenceRowCellIds?: ReadonlySet<string>;
  /** chunkId+정규화텍스트 → 세그먼트. 매칭된 문단을 대치 렌더 */
  matchedParagraphSegments?: ReadonlyMap<string, DsdEditorParagraphSegment[]>;
};

type DsdEditorCompanyNameReviewContent = {
  enabled: boolean;
  /** chunkId+정규화텍스트 → 세그먼트. 기업명 포함 문단을 대치 렌더 */
  paragraphSegments: ReadonlyMap<string, DsdEditorParagraphSegment[]>;
  /** cellId(valueItemRef) → 세그먼트. 기업명 포함 셀 값을 대치 렌더 */
  cellSegmentsByCellId: ReadonlyMap<string, DsdEditorParagraphSegment[]>;
  /** 확인 필요 표기 → 노랑, 그 외 초록 */
  cautionOccurrenceIds: ReadonlySet<string>;
  selectedOccurrenceId: string | null;
  onSelectOccurrence: (occurrenceId: string) => void;
};

type DsdEditorCorrectionReviewContent = {
  enabled: boolean;
  activeItemId: string | null;
  correctionBlockByItemId: ReadonlyMap<string, DsdEditorCorrectionBlock>;
  correctionBlockByCellId: ReadonlyMap<string, DsdEditorCorrectionBlock>;
  onSelect: (item: DsdEditorCorrectionItem) => void;
};

type DsdEditorSumValidationReviewContent = {
  enabled: boolean;
  selectedCellId: string | null;
  highlightedCellIds: ReadonlySet<string>;
  onSelectCell: (cellId: string) => void;
};

type DsdEditorDocumentContentProps = {
  frameClassName?: string;
  sections: string[];
  itemsBySection: ReadonlyMap<string, DsdEditorParsedItem[]>;
  selectedCellId: string | null;
  noteReferenceReview?: DsdEditorNoteReferenceReviewContent;
  correctionReview?: DsdEditorCorrectionReviewContent;
  sumValidationReview?: DsdEditorSumValidationReviewContent;
  companyNameReview?: DsdEditorCompanyNameReviewContent;
  onCellSelect?: (cellId: string) => void;
  /** 본문·주석매칭 표에서 클릭을 받을 셀. 없으면 모든 셀 */
  canSelectCell?: (cellId: string) => boolean;
  /** 같은 문서를 복수 viewport에 렌더할 때 DOM id 충돌을 막는다. */
  cellDomIdPrefix?: string;
  /** 주석 섹션 제목 줄(숫자로 시작하는 섹션명)을 그릴지. 제목을 따로 보여주는 창(근거 창)은 끈다 */
  showSectionTitle?: boolean;
};

const DsdEditorItemFrame = ({
  item,
  active = false,
  clickable = false,
  divider = false,
  onClick,
  children,
}: {
  item: DsdEditorParsedItem;
  active?: boolean;
  clickable?: boolean;
  // 다음 아이템이 다른 청크일 때만 아래 구분선 표시(같은 청크의 sub chunk 사이는 선 없음)
  divider?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  children: ReactNode;
}) => (
  <div
    data-dsd-chunk-id={item.chunkId}
    data-dsd-correction-id={item.id}
    className={cn(
      "overflow-clip bg-white px-3 py-3",
      divider && "border-b border-line-normal",
      active && "ring-2 ring-blue-600 ring-inset",
      clickable && "cursor-pointer",
    )}
    onClick={onClick}
  >
    {children}
  </div>
);

const DsdEditorNoteReferenceTable = ({
  item,
  selectedCellId,
  noteReferenceReview,
  onCellSelect,
  canSelectCell,
}: {
  item: Extract<DsdEditorParsedItem, { kind: "table" }>;
  selectedCellId: string | null;
  noteReferenceReview?: DsdEditorNoteReferenceReviewContent;
  onCellSelect?: (cellId: string) => void;
  canSelectCell?: (cellId: string) => boolean;
}) => {
  const getCellDomId = useDsdEditorCellDomId();
  const evidenceTone = noteReferenceReview?.evidencePreviewTone;
  const matchTone =
    evidenceTone === "match-positive"
      ? "positive"
      : evidenceTone === "match-caution"
        ? "caution"
        : evidenceTone === "match-negative"
          ? "negative"
          : null;
  const evidenceClassName =
    evidenceTone === "explanation"
      ? "bg-orange-100 ring-2 ring-orange-500 ring-inset"
      : evidenceTone === "value"
        ? "bg-violet-100 ring-2 ring-violet-500 ring-inset"
        : matchTone
          ? NOTE_REFERENCE_EVIDENCE_CELL_CLASS[matchTone]
          : "bg-blue-100 ring-2 ring-blue-500 ring-inset";
  const getCellClassName = (cell: PlainDsdCell) => {
    const isEvidencePreview =
      noteReferenceReview?.evidencePreviewCellIds?.has(cell.cellId) ?? false;
    const isEvidenceRow =
      !isEvidencePreview &&
      !!matchTone &&
      (noteReferenceReview?.evidenceRowCellIds?.has(cell.cellId) ?? false);
    const hardRuleCount =
      noteReferenceReview?.hardRuleCountByCellId?.get(cell.cellId) ?? 0;
    return cn(
      // 하드룰 셀은 빗금 — 배경 이미지라 상태 채움(배경색)·근거 미리보기 ring 과 겹쳐도 다 보인다
      hardRuleCount > 0 && NOTE_REFERENCE_HARD_RULE_CELL_CLASS,
      isEvidencePreview && evidenceClassName,
      isEvidenceRow && NOTE_REFERENCE_EVIDENCE_ROW_CLASS[matchTone],
    );
  };
  // 규칙이 2개 이상 걸린 셀(본문 셀)은 값 앞에 개수 칩 — 셀을 누르기 전에 몇 개 걸렸는지 보인다
  const renderCellValue = (cell: PlainDsdCell) => {
    const hardRuleCount =
      noteReferenceReview?.hardRuleCountByCellId?.get(cell.cellId) ?? 0;
    return hardRuleCount >= 2 ? (
      <DsdReviewHardRuleCountChip count={hardRuleCount}>
        {renderPlainDsdCellText(cell)}
      </DsdReviewHardRuleCountChip>
    ) : undefined;
  };
  return (
    <PlainDsdTableView
      renderCellValue={renderCellValue}
      table={item.table}
      selectedCellId={selectedCellId}
      dimExceptCellId={noteReferenceReview?.focusCellId}
      linkedCellIds={noteReferenceReview?.linkedCellIds}
      candidateCellIds={noteReferenceReview?.candidateCellIds}
      candidateStatusByCellId={noteReferenceReview?.candidateStatusByCellId}
      noteReferenceStatusByCellId={noteReferenceReview?.statusByCellId}
      noteReferenceLabelByStatus={noteReferenceReview?.statusLabels}
      getCellDomId={(cell) => getCellDomId(cell.cellId)}
      canSelectCell={canSelectCell && ((cell) => canSelectCell(cell.cellId))}
      onCellSelect={onCellSelect}
      getCellClassName={getCellClassName}
    />
  );
};

const DsdEditorCorrectionTable = ({
  item,
  selectedCellId,
  correctionReview,
  onCellSelect,
}: {
  item: Extract<DsdEditorParsedItem, { kind: "table" }>;
  selectedCellId: string | null;
  correctionReview: DsdEditorCorrectionReviewContent;
  onCellSelect?: (cellId: string) => void;
}) => {
  const getCellDomId = useDsdEditorCellDomId();
  const activeCorrectionCellId = correctionReview.correctionBlockByCellId.has(
    correctionReview.activeItemId ?? "",
  )
    ? correctionReview.activeItemId
    : null;

  const renderCellValue = (cell: PlainDsdCell) => {
    const correctionBlock = correctionReview.correctionBlockByCellId.get(
      cell.cellId,
    );

    // 교정 블록이 없으면 undefined → PlainDsdCellView 기본 텍스트(들여쓰기 포함)로 폴백
    return correctionBlock ? (
      <DsdReviewCorrectionCellDiff block={correctionBlock} />
    ) : undefined;
  };

  // 교정 블록이 있는 셀은 교정 선택, 나머지 일반 셀은 기본 탭과 같은 셀 선택.
  // 모델이 서로를 지워 주므로(교정 선택 ↔ 셀 선택) 하이라이트는 한 번에 하나만 남는다.
  const selectCell = (cellId: string) => {
    const correctionBlock =
      correctionReview.correctionBlockByCellId.get(cellId);
    if (!correctionBlock) {
      onCellSelect?.(cellId);
      return;
    }

    correctionReview.onSelect({
      id: cellId,
      kind: "cell",
      sectionName: item.sectionName,
      block: correctionBlock,
    });
  };

  return (
    <PlainDsdTableView
      table={item.table}
      selectedCellId={selectedCellId}
      activeCorrectionCellId={activeCorrectionCellId}
      getCellDomId={(cell) => getCellDomId(cell.cellId)}
      canSelectCell={(cell) =>
        correctionReview.correctionBlockByCellId.has(cell.cellId) ||
        !!onCellSelect
      }
      onCellSelect={selectCell}
      renderCellValue={renderCellValue}
    />
  );
};

const DsdEditorSumValidationTable = ({
  item,
  selectedCellId,
  sumValidationReview,
  onCellSelect,
}: {
  item: Extract<DsdEditorParsedItem, { kind: "table" }>;
  selectedCellId: string | null;
  sumValidationReview: DsdEditorSumValidationReviewContent;
  onCellSelect?: (cellId: string) => void;
}) => {
  const getCellDomId = useDsdEditorCellDomId();
  return (
    <PlainDsdTableView
      table={item.table}
      selectedCellId={selectedCellId}
      getCellDomId={(cell) => getCellDomId(cell.cellId)}
      // 합계 셀은 자기 클릭(onSumCellSelect)만 — 셀 선택까지 같이 타면 모델이 방금 만든
      // 합계 선택을 도로 지운다. 일반 셀만 기본 탭과 같은 셀 선택.
      canSelectCell={(cell) => !isSumInteractiveCell(cell)}
      onCellSelect={onCellSelect}
      sumValidationReview={{
        enabled: true,
        selectedCellId: sumValidationReview.selectedCellId,
        highlightedCellIds: sumValidationReview.highlightedCellIds,
        onSelectCell: (cell) => sumValidationReview.onSelectCell(cell.cellId),
      }}
    />
  );
};

// 기업명 표기 — 문단/셀 공용. 확인 필요면 노랑, 일치면 초록. 클릭 시 패널 선택과 동기화
const CompanyNameSegment = ({
  occurrenceId,
  text,
  review,
}: {
  occurrenceId: string;
  text: string;
  review: DsdEditorCompanyNameReviewContent;
}) => {
  const getCellDomId = useDsdEditorCellDomId();
  const fill = review.cautionOccurrenceIds.has(occurrenceId)
    ? "caution"
    : "positive";
  const selected = review.selectedOccurrenceId === occurrenceId;
  // 선택 중엔 나머지 표기를 흐리게 (본문-주석 매칭 포커스와 동일 규칙)
  const dimmed = !!review.selectedOccurrenceId && !selected;

  return (
    <span
      id={getCellDomId(occurrenceId)}
      data-cell-id={occurrenceId}
      role="button"
      tabIndex={0}
      onClick={(event) => {
        event.stopPropagation();
        review.onSelectOccurrence(occurrenceId);
      }}
      className={cn(
        "cursor-pointer rounded-[3px] px-0.5 py-0.5 -mx-0.5 transition-colors",
        dimmed
          ? NOTE_REFERENCE_CELL_FILL_DIM[fill]
          : NOTE_REFERENCE_CELL_FILL[fill],
        dimmed && "text-label-alternative",
        selected &&
          (fill === "caution"
            ? "ring-2 ring-yellow-500 ring-inset"
            : "ring-2 ring-green-500 ring-inset"),
      )}
    >
      {text}
    </span>
  );
};

const renderCompanyNameSegments = (
  segments: DsdEditorParagraphSegment[],
  review: DsdEditorCompanyNameReviewContent,
) =>
  segments.map((segment, index) =>
    segment.type === "company_name" && segment.occurrenceId ? (
      <CompanyNameSegment
        key={index}
        occurrenceId={segment.occurrenceId}
        text={segment.text}
        review={review}
      />
    ) : (
      <Fragment key={index}>{segment.text}</Fragment>
    ),
  );

const DsdEditorCompanyNameTable = ({
  item,
  selectedCellId,
  companyNameReview,
  onCellSelect,
}: {
  item: Extract<DsdEditorParsedItem, { kind: "table" }>;
  selectedCellId: string | null;
  companyNameReview: DsdEditorCompanyNameReviewContent;
  onCellSelect?: (cellId: string) => void;
}) => {
  const getCellDomId = useDsdEditorCellDomId();

  const renderCellValue = (cell: PlainDsdCell) => {
    const segments = companyNameReview.cellSegmentsByCellId.get(cell.cellId);
    // span 하나로 감싼다: 헤더 셀 안쪽이 flex(gap-1)라 Fragment면 세그먼트마다 flex 항목이 돼
    // 기업명 칸(py-0.5)만 높아지고 뒤 글자와 세로 정렬·간격이 어긋난다. 감싸면 문단처럼 인라인 흐름
    return segments ? (
      <span>{renderCompanyNameSegments(segments, companyNameReview)}</span>
    ) : undefined;
  };

  return (
    <PlainDsdTableView
      table={item.table}
      selectedCellId={selectedCellId}
      getCellDomId={(cell) => getCellDomId(cell.cellId)}
      // 세그먼트 클릭은 전파를 막으므로 셀 선택은 세그먼트 밖 클릭에만 걸린다.
      onCellSelect={onCellSelect}
      renderCellValue={renderCellValue}
    />
  );
};

const DsdEditorTableItemView = ({
  item,
  selectedCellId,
  noteReferenceReview,
  correctionReview,
  sumValidationReview,
  companyNameReview,
  onCellSelect,
  canSelectCell,
}: {
  item: Extract<DsdEditorParsedItem, { kind: "table" }>;
  selectedCellId: string | null;
  noteReferenceReview?: DsdEditorNoteReferenceReviewContent;
  correctionReview?: DsdEditorCorrectionReviewContent;
  sumValidationReview?: DsdEditorSumValidationReviewContent;
  companyNameReview?: DsdEditorCompanyNameReviewContent;
  onCellSelect?: (cellId: string) => void;
  canSelectCell?: (cellId: string) => boolean;
}) => {
  if (companyNameReview?.enabled) {
    return (
      <DsdEditorCompanyNameTable
        item={item}
        selectedCellId={selectedCellId}
        onCellSelect={onCellSelect}
        companyNameReview={companyNameReview}
      />
    );
  }

  if (correctionReview?.enabled) {
    return (
      <DsdEditorCorrectionTable
        item={item}
        selectedCellId={selectedCellId}
        onCellSelect={onCellSelect}
        correctionReview={correctionReview}
      />
    );
  }

  if (sumValidationReview?.enabled) {
    return (
      <DsdEditorSumValidationTable
        item={item}
        selectedCellId={selectedCellId}
        onCellSelect={onCellSelect}
        sumValidationReview={sumValidationReview}
      />
    );
  }

  return (
    <DsdEditorNoteReferenceTable
      item={item}
      selectedCellId={selectedCellId}
      noteReferenceReview={noteReferenceReview}
      onCellSelect={onCellSelect}
      canSelectCell={canSelectCell}
    />
  );
};

// 문단 내 매칭된 값 — 셀처럼 상태색 + 클릭(선택)/스크롤(DOM id)
const ParagraphValueSegment = ({
  occurrenceId,
  text,
  status,
  candidateStatus,
  selected,
  dimmed,
  onSelect,
}: {
  occurrenceId: string;
  text: string;
  status?: PlainDsdNoteReferenceStatus;
  candidateStatus?: DsdNoteReferenceCandidateTone;
  selected: boolean;
  dimmed: boolean;
  onSelect?: (cellId: string) => void;
}) => {
  const getCellDomId = useDsdEditorCellDomId();
  const meta = status ? NOTE_REFERENCE_STATUS_META[status] : null;
  // 셀과 동일 규칙: 상태 있으면 상태색, 없는 후보(문장에서 찾은 값)는 후보색(초록/노랑)
  const candidateFill = candidateStatus ?? "positive";
  const fill = meta
    ? dimmed
      ? meta.dimBgClassName
      : meta.bgClassName
    : dimmed
      ? NOTE_REFERENCE_CELL_FILL_DIM[candidateFill]
      : NOTE_REFERENCE_CELL_FILL[candidateFill];

  return (
    <span
      id={getCellDomId(occurrenceId)}
      data-cell-id={occurrenceId}
      role="button"
      tabIndex={0}
      onClick={(event) => {
        event.stopPropagation();
        onSelect?.(occurrenceId);
      }}
      className={cn(
        // 여백은 주되 음수 margin으로 레이아웃 밀림 상쇄(테두리~글자 사이만 벌림)
        "cursor-pointer rounded-[3px] px-0.5 py-0.5 -mx-0.5 transition-colors",
        fill,
        dimmed && "text-label-alternative",
        // 상태 있는 값은 neutral, 후보(no meta)는 후보색 따라 초록/노랑/빨강 테두리
        selected &&
          (meta
            ? "ring-2 ring-neutral-400 ring-inset"
            : candidateFill === "negative"
              ? "ring-2 ring-rose-500 ring-inset"
              : candidateFill === "caution"
                ? "ring-2 ring-yellow-500 ring-inset"
                : "ring-2 ring-green-500 ring-inset"),
      )}
    >
      {text}
    </span>
  );
};

const DsdEditorParagraphItemView = ({
  item,
  noteReferenceReview,
  selectedCellId,
  correctionReview,
  companyNameReview,
  onCellSelect,
}: {
  item: Extract<DsdEditorParsedItem, { kind: "paragraph" }>;
  noteReferenceReview?: DsdEditorNoteReferenceReviewContent;
  selectedCellId: string | null;
  correctionReview?: DsdEditorCorrectionReviewContent;
  companyNameReview?: DsdEditorCompanyNameReviewContent;
  onCellSelect?: (cellId: string) => void;
}) => {
  // 기업명 검토 탭: 기업명 포함 문단은 세그먼트로 대치 렌더(값 매칭 문단과 동일 방식).
  if (companyNameReview?.enabled) {
    const companySegments = companyNameReview.paragraphSegments.get(
      matchedParagraphKey(item.chunkId, item.text),
    );

    if (companySegments) {
      return (
        <DsdTextBlock>
          <span>
            {renderCompanyNameSegments(companySegments, companyNameReview)}
          </span>
        </DsdTextBlock>
      );
    }
  }
  const correctionBlock = correctionReview?.enabled
    ? correctionReview.correctionBlockByItemId.get(item.id)
    : undefined;

  if (correctionBlock) {
    return <DsdReviewCorrectionInlineDiff block={correctionBlock} />;
  }

  const segments = noteReferenceReview?.matchedParagraphSegments?.get(
    matchedParagraphKey(item.chunkId, item.text),
  );

  if (!segments) {
    return <DsdTextBlock>{item.text}</DsdTextBlock>;
  }

  const focusCellId = noteReferenceReview?.focusCellId;

  return (
    <DsdTextBlock>
      {/* 인라인 흐름 유지 위해 단일 span으로 감싼다 */}
      <span>
        {segments.map((segment, index) =>
          segment.type === "value" && segment.occurrenceId ? (
            <ParagraphValueSegment
              key={index}
              occurrenceId={segment.occurrenceId}
              text={segment.text}
              status={noteReferenceReview?.statusByCellId.get(
                segment.occurrenceId,
              )}
              candidateStatus={noteReferenceReview?.candidateStatusByCellId?.get(
                segment.occurrenceId,
              )}
              selected={selectedCellId === segment.occurrenceId}
              dimmed={!!focusCellId && focusCellId !== segment.occurrenceId}
              onSelect={onCellSelect}
            />
          ) : (
            <Fragment key={index}>{segment.text}</Fragment>
          ),
        )}
      </span>
    </DsdTextBlock>
  );
};

// 이 문서는 셀이 수천 개라, 클릭 한 번의 선택 변경을 모든 아이템에 그대로 흘리면
// 카드 전체가 리렌더돼 수백 ms 가 걸린다. 선택은 항상 한두 아이템에만 걸리므로,
// 상위에서 선택 상태를 아이템 단위로 쪼개 내려주고(무관한 아이템은 참조가 고정된
// idle 객체) 여기서 memo 로 걸러 낸다. DSD 대사 뷰(DiffItemView)와 같은 처방.
const DsdEditorDocumentItemView = memo(function DsdEditorDocumentItemView({
  item,
  divider,
  selectedCellId,
  noteReferenceReview,
  correctionReview,
  sumValidationReview,
  companyNameReview,
  onCellSelect,
  canSelectCell,
}: {
  item: DsdEditorParsedItem;
  divider?: boolean;
  selectedCellId: string | null;
  noteReferenceReview?: DsdEditorNoteReferenceReviewContent;
  correctionReview?: DsdEditorCorrectionReviewContent;
  sumValidationReview?: DsdEditorSumValidationReviewContent;
  companyNameReview?: DsdEditorCompanyNameReviewContent;
  onCellSelect?: (cellId: string) => void;
  canSelectCell?: (cellId: string) => boolean;
}) {
  const paragraphCorrectionBlock =
    item.kind === "paragraph" && correctionReview?.enabled
      ? correctionReview.correctionBlockByItemId.get(item.id)
      : undefined;
  const activeParagraphCorrection =
    !!paragraphCorrectionBlock && correctionReview?.activeItemId === item.id;

  const selectParagraphCorrection = paragraphCorrectionBlock
    ? (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        correctionReview?.onSelect({
          id: item.id,
          kind: "paragraph",
          sectionName: item.sectionName,
          block: paragraphCorrectionBlock,
        });
      }
    : undefined;

  return (
    <DsdEditorItemFrame
      item={item}
      active={activeParagraphCorrection}
      clickable={!!paragraphCorrectionBlock}
      divider={divider}
      onClick={selectParagraphCorrection}
    >
      {item.kind === "table" ? (
        <DsdEditorTableItemView
          item={item}
          selectedCellId={selectedCellId}
          noteReferenceReview={noteReferenceReview}
          correctionReview={correctionReview}
          sumValidationReview={sumValidationReview}
          companyNameReview={companyNameReview}
          onCellSelect={onCellSelect}
          canSelectCell={canSelectCell}
        />
      ) : (
        <DsdEditorParagraphItemView
          item={item}
          noteReferenceReview={noteReferenceReview}
          selectedCellId={selectedCellId}
          correctionReview={correctionReview}
          companyNameReview={companyNameReview}
          onCellSelect={onCellSelect}
        />
      )}
    </DsdEditorItemFrame>
  );
});

/** 선택이 걸리지 않은 아이템들이 공유하는 빈 집합(참조 고정 — memo 통과용). */
const EMPTY_CELL_IDS: ReadonlySet<string> = new Set();

/**
 * 셀/문단 값 아이템 id → 소속 아이템 id. 선택 상태를 아이템 단위로 쪼개기 위한 색인.
 * 표는 셀 전부, 문단은 값 아이템 하나(valueItemId)를 소유한다.
 */
const buildCellOwnerIndex = (
  itemsBySection: ReadonlyMap<string, DsdEditorParsedItem[]>,
): ReadonlyMap<string, string> => {
  const owners = new Map<string, string>();
  for (const items of itemsBySection.values()) {
    for (const item of items) {
      if (item.kind === "table") {
        for (const row of item.table.rows) {
          for (const cell of row.cells) owners.set(cell.cellId, item.id);
        }
      } else if (item.valueItemId) {
        owners.set(item.valueItemId, item.id);
      }
    }
  }
  return owners;
};

/** 문서 전체 셀 집합을 소속 아이템별로 나눈다. 없는 아이템은 키가 생기지 않는다. */
const groupCellIdsByItem = (
  cellIds: ReadonlySet<string> | undefined,
  owners: ReadonlyMap<string, string>,
): ReadonlyMap<string, ReadonlySet<string>> => {
  const grouped = new Map<string, Set<string>>();
  if (!cellIds) return grouped;
  for (const cellId of cellIds) {
    const itemId = owners.get(cellId);
    if (!itemId) continue;
    let bucket = grouped.get(itemId);
    if (!bucket) {
      bucket = new Set<string>();
      grouped.set(itemId, bucket);
    }
    bucket.add(cellId);
  }
  return grouped;
};

export const DsdEditorDocumentContent = ({
  sections,
  itemsBySection,
  selectedCellId,
  noteReferenceReview,
  correctionReview,
  sumValidationReview,
  companyNameReview,
  onCellSelect,
  canSelectCell,
  cellDomIdPrefix = "dsd-editor-cell",
  showSectionTitle = true,
  frameClassName,
}: DsdEditorDocumentContentProps) => {
  // 문서 순서상 다음 아이템이 다른 청크면 해당 아이템 뒤에 구분선을 둔다.
  // sections/itemsBySection에만 의존하므로 memo로 잦은 리렌더(선택·호버) 재계산 방지
  const dividerIds = useMemo(() => {
    const ordered = sections.flatMap(
      (sectionName) => itemsBySection.get(sectionName) ?? [],
    );
    const ids = new Set(
      ordered
        .filter((item, i) => ordered[i + 1]?.chunkId !== item.chunkId)
        .map((item) => item.id),
    );
    // 마지막 아이템은 다음이 없어 위 필터에 걸리지만, 바깥 테두리가 닫으므로 선 불필요
    if (ordered.length) ids.delete(ordered[ordered.length - 1].id);
    return ids;
  }, [sections, itemsBySection]);

  // ── 아이템 단위 리렌더 최소화 ──
  // 뷰모델은 리뷰 콘텐츠를 매 렌더 새 객체 리터럴로 만들고 핸들러도 인라인 화살표라,
  // 그대로 내려보내면 memo(DsdEditorDocumentItemView)가 한 번도 통과되지 않는다.
  // 여기서 (1) 콜백을 최신값 ref 로 감싸 참조를 고정하고 (2) 선택 상태를 아이템별로
  // 쪼개, 선택이 안 걸린 아이템에는 렌더 간 동일한 객체를 넘긴다.

  // 최신 핸들러를 ref 로 들고 있으면, 아래 useCallback 을 의존성 없이 고정할 수 있다.
  const handlersRef = useRef({
    onCellSelect,
    onCorrectionSelect: correctionReview?.onSelect,
    onSumCellSelect: sumValidationReview?.onSelectCell,
    onSelectOccurrence: companyNameReview?.onSelectOccurrence,
  });
  handlersRef.current = {
    onCellSelect,
    onCorrectionSelect: correctionReview?.onSelect,
    onSumCellSelect: sumValidationReview?.onSelectCell,
    onSelectOccurrence: companyNameReview?.onSelectOccurrence,
  };

  const stableOnCellSelect = useCallback((cellId: string) => {
    handlersRef.current.onCellSelect?.(cellId);
  }, []);
  const stableOnCorrectionSelect = useCallback(
    (item: DsdEditorCorrectionItem) => {
      handlersRef.current.onCorrectionSelect?.(item);
    },
    [],
  );
  const stableOnSumCellSelect = useCallback((cellId: string) => {
    handlersRef.current.onSumCellSelect?.(cellId);
  }, []);
  const stableOnSelectOccurrence = useCallback((occurrenceId: string) => {
    handlersRef.current.onSelectOccurrence?.(occurrenceId);
  }, []);
  // 리뷰 콘텐츠를 "선택과 무관한 부분"만으로 다시 조립한다. 하위 필드(Map/Set)는
  // 이미 상위 훅에서 memo 되어 있어, 셀 클릭으로는 이 객체들의 참조가 바뀌지 않는다.
  const correctionContent = useMemo(
    () =>
      correctionReview && {
        enabled: correctionReview.enabled,
        activeItemId: correctionReview.activeItemId,
        correctionBlockByItemId: correctionReview.correctionBlockByItemId,
        correctionBlockByCellId: correctionReview.correctionBlockByCellId,
        onSelect: stableOnCorrectionSelect,
      },
    // 객체 자체는 매 렌더 새로 만들어지므로 의도적으로 하위 필드에만 의존한다 —
    // 참조를 고정하는 게 이 memo 의 목적이라, 객체를 의존성에 넣으면 무의미해진다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      correctionReview?.enabled,
      correctionReview?.activeItemId,
      correctionReview?.correctionBlockByItemId,
      correctionReview?.correctionBlockByCellId,
      stableOnCorrectionSelect,
    ],
  );

  const sumValidationContent = useMemo(
    () =>
      sumValidationReview && {
        enabled: sumValidationReview.enabled,
        selectedCellId: sumValidationReview.selectedCellId,
        highlightedCellIds: sumValidationReview.highlightedCellIds,
        onSelectCell: stableOnSumCellSelect,
      },
    // 위와 같은 이유로 하위 필드에만 의존한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sumValidationReview?.enabled,
      sumValidationReview?.selectedCellId,
      sumValidationReview?.highlightedCellIds,
      stableOnSumCellSelect,
    ],
  );

  const companyNameContent = useMemo(
    () =>
      companyNameReview && {
        enabled: companyNameReview.enabled,
        paragraphSegments: companyNameReview.paragraphSegments,
        cellSegmentsByCellId: companyNameReview.cellSegmentsByCellId,
        cautionOccurrenceIds: companyNameReview.cautionOccurrenceIds,
        selectedOccurrenceId: companyNameReview.selectedOccurrenceId,
        onSelectOccurrence: stableOnSelectOccurrence,
      },
    // 위와 같은 이유로 하위 필드에만 의존한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      companyNameReview?.enabled,
      companyNameReview?.paragraphSegments,
      companyNameReview?.cellSegmentsByCellId,
      companyNameReview?.cautionOccurrenceIds,
      companyNameReview?.selectedOccurrenceId,
      stableOnSelectOccurrence,
    ],
  );

  // 셀 → 소속 아이템 색인과, 선택 셀 집합의 아이템별 분할
  const cellOwnerByCellId = useMemo(
    () => buildCellOwnerIndex(itemsBySection),
    [itemsBySection],
  );
  const linkedByItem = useMemo(
    () =>
      groupCellIdsByItem(noteReferenceReview?.linkedCellIds, cellOwnerByCellId),
    [noteReferenceReview?.linkedCellIds, cellOwnerByCellId],
  );
  const focusCellId = noteReferenceReview?.focusCellId ?? null;
  const selectedItemId = selectedCellId
    ? (cellOwnerByCellId.get(selectedCellId) ?? null)
    : null;
  const focusItemId = focusCellId
    ? (cellOwnerByCellId.get(focusCellId) ?? null)
    : null;

  // 선택이 안 걸린 아이템 전부가 공유하는 noteReferenceReview. 선택 파생 필드
  // (linkedCellIds·focusCellId)를 비워 두면 렌더 간 참조가 고정된다.
  // 필드를 열거해 새 객체를 짓지 않고 **spread 로 통째로 옮긴 뒤** 선택 파생 필드만
  // 덮는다. 열거하면 나중에 추가된 필드가 조용히 빠진다 — 실제로 그렇게 깨졌다
  // (evidencePreviewCellIds 누락 → 부차 문서 뷰의 근거 셀 강조가 안 칠해짐).
  const idleNoteReferenceContent = useMemo(
    () =>
      noteReferenceReview && {
        ...noteReferenceReview,
        linkedCellIds: EMPTY_CELL_IDS,
        focusCellId: null,
      },
    // 위와 같은 이유로 객체가 아니라 하위 필드에 의존한다(부모가 매 렌더 새 객체를
    // 만들어 참조가 안 고정된다). **필드를 추가하면 여기에도 넣을 것** — 빠뜨리면
    // 값이 안 실리는 게 아니라 갱신이 늦는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      noteReferenceReview?.candidateCellIds,
      noteReferenceReview?.candidateStatusByCellId,
      noteReferenceReview?.statusByCellId,
      noteReferenceReview?.hardRuleCountByCellId,
      noteReferenceReview?.matchedParagraphSegments,
      noteReferenceReview?.evidencePreviewCellIds,
      noteReferenceReview?.evidencePreviewTone,
      noteReferenceReview?.evidenceRowCellIds,
    ],
  );

  /** 선택이 걸린 아이템에만 실제 선택값을 실어 준다. 나머지는 idle 객체 그대로. */
  const noteReferenceContentFor = (itemId: string) => {
    if (!idleNoteReferenceContent) return undefined;
    const linked = linkedByItem.get(itemId);
    const focused = focusItemId === itemId;
    if (!linked && !focused) return idleNoteReferenceContent;
    return {
      ...idleNoteReferenceContent,
      linkedCellIds: linked ?? EMPTY_CELL_IDS,
      focusCellId: focused ? focusCellId : null,
    };
  };

  return (
    <DsdEditorCellDomIdPrefixContext.Provider value={cellDomIdPrefix}>
      <div
        className={cn(
          "overflow-clip border border-line-normal",
          frameClassName,
        )}
      >
        {sections.map((sectionName) => (
          <section
            key={sectionName}
            data-dsd-section={sectionName}
            className="flex flex-col"
          >
            {/* 청크(=섹션) 제목: 예전엔 subChunk html 선두에 있었으나 이제 title에서 분리됨 */}
            {/* 제목은 주석(숫자로 시작)에서만 노출. 재무제표 본문에는 미표시 */}
            {showSectionTitle && /^\d/.test(sectionName) && (
              // 여백은 서브청크(DsdTextBlock)와 동일: 프레임 px-3 py-3 + 내부 px-2 py-1. 제목이라 굵기만 semibold
              <div className="bg-white px-3 py-3">
                <div className="label-2-semibold px-2 py-1 text-label-normal">
                  {sectionName}
                </div>
              </div>
            )}
            {(itemsBySection.get(sectionName) ?? []).map((item) => (
              <DsdEditorDocumentItemView
                key={item.id}
                item={item}
                divider={dividerIds.has(item.id)}
                selectedCellId={
                  selectedItemId === item.id ? selectedCellId : null
                }
                noteReferenceReview={noteReferenceContentFor(item.id)}
                correctionReview={correctionContent}
                sumValidationReview={sumValidationContent}
                companyNameReview={companyNameContent}
                onCellSelect={stableOnCellSelect}
                // 렌더 결과(커서·클릭 가능)를 정하는 술어라 ref 로 고정하지 않는다 — 바뀌면 memo 가 깨져야 맞다. 호출측이 useCallback 으로 참조를 관리한다
                canSelectCell={canSelectCell}
              />
            ))}
          </section>
        ))}
      </div>
    </DsdEditorCellDomIdPrefixContext.Provider>
  );
};
